import React, { useState } from "react";
import { PieChart, Pie, Legend, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";
import { Box, Tooltip, useTheme } from "@mui/material";

import WidgetAlert from "components/widgetParts/WidgetAlert";
import { defaultSettings } from "./PieChartSettings";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import { Query } from "system/query/Query";
import { DashboardQueryDrilldownDialog } from "components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import PieChartTooltip from "./PieChartTooltip";
import { getFullFieldToColor } from "theme/KeyToInfegyColor";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from "system/widget/Widget";

// import "./styles.css";

const wrapperStyle = {
  width: "max-content",
  maxWidth: "300px",
};

interface PieSlice {
  name: string;
  value: number;
  fill: string;
}

const RADIAN = Math.PI / 180;
export const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }: any, labelType: string, onClick: () => void) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const displayValue = labelType === 'distribution' ? `${(percent * 100).toFixed(0)}%` : value;

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" onClick={() => {
      onClick();
    }}>
      {displayValue}
    </text>
  );
};

function PieChartContent(props: WithQueryWidgetProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'pie-chart');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'pie-chart');

  const aggs = originalRequest?.requestInit?.body?.aggs || {};

  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [drilldownIsOpen, setDrilldownIsOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  const [hoveredSlice, setHoveredSlice] = useState<any>(null);

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};
  const widgetLabel = Object.keys(aggs)[0];
  const firstAggKey = Object.keys(aggs)[0];
  const apiResponse = apiResponseInfo?.responseBody || {};
  const apiDataValues: number[] = apiResponse?.[firstAggKey]?.series?.[0]?.values ?? [];
  const chartColors = theme.colors.namedColors.chartColorArray;
  const defaultMaxSliceCount = 10;

  let pieData: PieSlice[] = [];
  let sumFilteredPieDataValues = 0;

  // If the api response has data, create the pie chart data, sort it, and calculate the sum of the filtered values
  if (apiDataValues.length > 0) {
    pieData = apiDataValues.reduce((acc: PieSlice[], current: number, index: number): PieSlice[] => {
      if (index < defaultMaxSliceCount) {
        sumFilteredPieDataValues += current;

        const label = apiResponse?.[firstAggKey].labels?.[index];
        const key = apiResponse?.[firstAggKey].keys?.[index];
        const series = apiResponse?.[firstAggKey].series?.[0];
        const fieldId = series.field_id;

        let fillColor = chartColors[index];
        if (fieldId && key && getFullFieldToColor(theme, fieldId, key)) {
          fillColor = getFullFieldToColor(theme, fieldId, key) || chartColors[index];
        }

        acc.push({
          name: label,
          value: current,
          fill: fillColor
        });
      }

      return acc;
    }, []).sort((a: PieSlice, b: PieSlice) => b.value - a.value);
  }

  // Keeps only the slices based off the max amount to display
  let maxSlicesPieData = pieData.slice(0, currentState.maximumSlices);

  // Handles when user selects to view slices that are only above a certain percentage
  if (currentState.minimumPortion > 0) {
    maxSlicesPieData = maxSlicesPieData.filter(slice => {
      const sliceAsPortion = Math.round((slice.value / sumFilteredPieDataValues) * 100);
      return sliceAsPortion >= currentState.minimumPortion;
    });
  }

  const maxSlicesPieDataSum = maxSlicesPieData.reduce((acc, curr) => acc + curr.value, 0);

  // Add "Other" group object w/ its value as the difference between total sum & current slices.
  if (maxSlicesPieDataSum < sumFilteredPieDataValues) {
    maxSlicesPieData = [
      ...maxSlicesPieData,
      { value: sumFilteredPieDataValues - maxSlicesPieDataSum, name: i18n.t("other"), fill: chartColors[defaultMaxSliceCount] }
    ];
  }

  const firstApiResponse = apiResponseInfo?.responseBody || {};
  const agg = firstApiResponse?.[firstAggKey] || [];
  const label = agg?.labels?.[selectedIndex ?? 0];
  const field_id = agg?.series?.[0]?.field_id;
  const drilldownQuery = new Query({
    query: {
        op: "contains",
        fields: [field_id],
        values: [agg.keys[selectedIndex ?? 0]]
      }
    }
  );

  const hideFocusVisible = `
    .recharts-layer:focus-visible {
      outline: none !important;
    }
  `;

  return (
    <>
    <style>{hideFocusVisible}</style>
    {pieData.length > 0 ?
      <WidgetLayout
        header={
          <WidgetTitle
            alignment={currentState.widgetTitleAlignment}
            customTitle={currentState.customWidgetTitle}
            defaultTitle={widgetLabel}
            settingsTitle={currentState.widgetTitle}
            widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
        }
      >
        <Tooltip
          followCursor={true}
          open={Boolean(hoveredSlice)}
          title={hoveredSlice ? <PieChartTooltip slice={hoveredSlice} /> : null}
        >
          <Box sx={{ position: "relative", height: "100%" }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  activeIndex={activeIndex}
                  data={maxSlicesPieData}
                  dataKey="value"
                  endAngle={-270}
                  fill={theme.palette.primary.main}
                  innerRadius={`${currentState.innerRadius}%`}
                  label={currentState.showLabels
                    ? item => renderCustomizedLabel(item, currentState.labelType, () => {
                      setSelectedIndex(item.index);
                      setDrilldownIsOpen(true)
                    })
                    : false}
                  labelLine={false}
                  onClick={(data) => {
                    setSelectedIndex(activeIndex);
                    setDrilldownIsOpen(true);
                  }}
                  onMouseEnter={(o: object, index: number) => {
                    setActiveIndex(index);
                    setHoveredSlice(o);
                  }}
                  onMouseLeave={() => {
                    setActiveIndex(undefined);
                    setHoveredSlice(null);
                  }}
                  startAngle={90}
                  style={{ cursor: "pointer", outline: 'none' }}
                />
                {/* <Tooltip formatter={(value: number) => Math.round((value / sumFilteredPieDataValues) * 100) + '%'} /> */}
                {currentState.showLegend &&
                  <Legend
                    align={currentState.legendPosition.align}
                    formatter={value => <span style={{ color: theme.palette.text.primary }}>{value}</span>}
                    layout={currentState.legendDirection}
                    onMouseEnter={(o: object, index: number) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(undefined)}
                    verticalAlign={currentState.legendPosition.verticalAlign}
                    wrapperStyle={currentState.legendPosition.verticalAlign === "middle" ? wrapperStyle : {}} />
                }
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Tooltip>
        {drilldownIsOpen &&
          <DashboardQueryDrilldownDialog
            title={label}
            queryInfo={props.queryInfo}
            onClose={() => {
              setSelectedIndex(undefined);
              setDrilldownIsOpen(false)}
            }
            activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
            drilldownQuery={drilldownQuery}
            widgetRequestInfo={originalRequest} />
        }
      </WidgetLayout>
      :
      <WidgetAlert severity="warning" alertTitleText={"api-request.no-content-message"} />}
    </>
  )
}

export default withQueryWidget(PieChartContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true,
});
